import { customFieldOptions } from '@/lib/custom-field-options';
import { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type {
    CustomField,
    FilterRule,
    TaskStatus,
    TaskPriority,
} from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import {
    getEnumValueLabel,
    getFieldLabel,
    getOperatorLabel,
    type TranslateFn,
} from './kanban-i18n';

// ============================================
// ADD COLUMN DIALOG
// ============================================
//
// Mirrors the TaskFilters bar's UX so a user who already knows how to
// filter the table recognises the controls. Each filterRule is one row;
// we stack them with logical AND semantics (a task must match every
// rule to land in the new column).

const FIELD_PRESETS: Array<{ value: string; label: string; operators: FilterRule['operator'][] }> = [
    { value: 'status', label: 'Status', operators: ['eq', 'neq', 'in', 'notIn', 'isEmpty', 'isNotEmpty'] },
    { value: 'priority', label: 'Priority', operators: ['eq', 'neq', 'in', 'notIn', 'isEmpty', 'isNotEmpty'] },
    { value: 'assignee.id', label: 'Assignee', operators: ['eq', 'neq', 'in', 'notIn', 'isEmpty', 'isNotEmpty'] },
    { value: 'customField', label: 'Custom field', operators: ['eq', 'neq', 'in', 'notIn', 'isEmpty', 'isNotEmpty', 'contains'] },
];

const STATUS_OPTIONS: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CANCELLED'];
const PRIORITY_OPTIONS: TaskPriority[] = ['NONE', 'LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const PRESET_COLORS = [
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#f43f5e', // rose
    '#f97316', // orange
    '#eab308', // yellow
    '#22c55e', // green
    '#14b8a6', // teal
    '#06b6d4', // cyan
    '#3b82f6', // blue
    '#64748b', // slate
];

interface AddColumnDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (data: { name: string; color?: string; filterRules: FilterRule[] }) => Promise<void> | void;
    customFields: CustomField[];
    projectMembers?: Array<{ userId: string; user: { id: string; name: string } }>;
    isLoading?: boolean;
}

function defaultRule(): FilterRule {
    return { field: 'status', operator: 'eq', value: 'TODO' };
}

export function AddColumnDialog({
    open,
    onOpenChange,
    onSubmit,
    customFields,
    projectMembers = [],
    isLoading,
}: AddColumnDialogProps) {
    const { t } = useI18n();
    const [name, setName] = useState('');
    const [color, setColor] = useState<string | undefined>(undefined);
    const [rules, setRules] = useState<FilterRule[]>([defaultRule()]);

    // Reset form whenever the dialog is opened so a previously cancelled
    // edit doesn't leak into the next attempt.
    useEffect(() => {
        if (open) {
            setName('');
            setColor(undefined);
            setRules([defaultRule()]);
        }
    }, [open]);

    const updateRule = (idx: number, patch: Partial<FilterRule>) => {
        setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };

    const removeRule = (idx: number) => {
        setRules((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
    };

    const addRule = () => {
        setRules((prev) => [...prev, defaultRule()]);
    };

    const handleSubmit = async () => {
        if (!name.trim() || rules.length === 0) return;
        await onSubmit({
            name: name.trim(),
            color,
            filterRules: rules,
        });
        onOpenChange(false);
    };

    const isCustomFieldValue = rules.some((r) => r.field === 'customField');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('kanban.addColumnTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('kanban.addColumnDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="col-name">{t('kanban.columnName')}</Label>
                        <Input
                            id="col-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('kanban.columnNamePlaceholder')}
                            autoFocus
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>{t('kanban.columnColor')}</Label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                className={`h-7 w-7 rounded-full border ${!color ? 'ring-2 ring-primary' : 'border-white/20'}`}
                                style={{ background: 'linear-gradient(45deg, #6366f1, #ec4899)' }}
                                aria-label="No color"
                                onClick={() => setColor(undefined)}
                            />
                            {PRESET_COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    aria-label={`Color ${c}`}
                                    className={`h-7 w-7 rounded-full border ${color === c ? 'ring-2 ring-primary' : 'border-white/20'}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setColor(c)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label>{t('kanban.columnFilterRules')}</Label>
                            <Button variant="ghost" size="sm" onClick={addRule} type="button">
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                {t('kanban.addRule')}
                            </Button>
                        </div>

                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                            {rules.map((rule, idx) => (
                                <AddColumnRuleRow
                                    key={idx}
                                    rule={rule}
                                    customFields={customFields}
                                    projectMembers={projectMembers}
                                    onChange={(patch) => updateRule(idx, patch)}
                                    onRemove={() => removeRule(idx)}
                                    canRemove={rules.length > 1}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isLoading || !name.trim() || rules.length === 0 || isCustomFieldValue}
                    >
                        {t('common.create')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// One FilterRule row inside the Add / Edit Column dialog. Exported so the
// EditColumnDialog can reuse the exact same row layout without
// duplicating ~100 lines of selector wiring.
export function AddColumnRuleRow({
    rule,
    customFields,
    projectMembers,
    onChange,
    onRemove,
    canRemove,
}: {
    rule: FilterRule;
    customFields: CustomField[];
    projectMembers: Array<{ userId: string; user: { id: string; name: string } }>;
    onChange: (patch: Partial<FilterRule>) => void;
    onRemove: () => void;
    canRemove: boolean;
}) {
    const { t } = useI18n();

    // When the user picks "customField", we follow up with a second
    // <Select> for the actual custom field id. We encode the choice as
    // `field = "customField.<id>"` so the rest of the pipeline doesn't
    // need to know about this UI quirk.
    const legacyCustomField = customFields.find(field => field.id === rule.field);
    const isCustomField = rule.field === 'customField' || rule.field.startsWith('customField.') || !!legacyCustomField;
    const customFieldId = isCustomField && rule.field !== 'customField'
        ? (legacyCustomField?.id ?? rule.field.slice('customField.'.length))
        : '';

    // t12: translated labels for the field/operator/value selectors so
    // users see "Statut" / "equals" / "À faire" in French etc. Custom
    // field names come from the user-set `customField.name` and don't
    // need translation.
    const fieldOptions = useMemo(() => {
        const base = FIELD_PRESETS.map((p) => ({
            value: p.value,
            label: getFieldLabel(p.value === 'customField' ? 'customField' : p.value, customFields, t),
        }));
        if (customFields.length > 0 && !isCustomField) {
            // Once a custom field has been picked we lock the selector
            // to "customField.<id>" — users can change which custom
            // field via the second selector below.
        }
        return base;
    }, [customFields, isCustomField, t]);

    const preset = FIELD_PRESETS.find((p) => p.value === (isCustomField ? 'customField' : rule.field));

    return (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 p-2">
            <Select
                value={isCustomField ? 'customField' : rule.field}
                onValueChange={(value) => {
                    if (value === 'customField') {
                        const firstCf = customFields[0];
                        if (firstCf) {
                            onChange({ field: `customField.${firstCf.id}`, value: undefined });
                        } else {
                            onChange({ field: 'customField', value: undefined });
                        }
                    } else {
                        onChange({ field: value, value: getDefaultValueForField(value) });
                    }
                }}
            >
                <SelectTrigger className="w-32">
                    <SelectValue placeholder="Field" />
                </SelectTrigger>
                <SelectContent>
                    {fieldOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {isCustomField && (
                <Select
                    value={customFieldId}
                    onValueChange={(value) => onChange({ field: `customField.${value}`, value: undefined })}
                >
                    <SelectTrigger className="w-40">
                        <SelectValue placeholder="Custom field" />
                    </SelectTrigger>
                    <SelectContent>
                        {customFields.map((cf) => (
                            <SelectItem key={cf.id} value={cf.id}>
                                {cf.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}

            <Select
                value={rule.operator}
                onValueChange={(value) => {
                    const op = value as FilterRule['operator'];
                    onChange({ operator: op, value: getDefaultValueForOperator(op, rule.field) });
                }}
            >
                <SelectTrigger className="w-32">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {/* t12: show translated operator verbs. */}
                    {(preset?.operators || ['eq']).map((op) => (
                        <SelectItem key={op} value={op}>
                            {getOperatorLabel(op, t)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {!['isEmpty', 'isNotEmpty'].includes(rule.operator) && (
                <ValueInput
                    rule={isCustomField && customFieldId ? { ...rule, field: `customField.${customFieldId}` } : rule}
                    projectMembers={projectMembers}
                    customFields={customFields}
                    onChange={onChange}
                    t={t}
                />
            )}

            {canRemove && (
                <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={onRemove} aria-label="Remove rule">
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            )}
        </div>
    );
}

function getDefaultValueForField(field: string): string | string[] | undefined {
    if (field === 'status') return 'TODO';
    if (field === 'priority') return 'NONE';
    if (field === 'assignee.id') return '';
    return undefined;
}

function getDefaultValueForOperator(op: FilterRule['operator'], field: string): string | string[] | undefined {
    if (op === 'isEmpty' || op === 'isNotEmpty') return undefined;
    if (op === 'in' || op === 'notIn') return getDefaultValueForField(field) !== undefined ? [getDefaultValueForField(field) as string] : [];
    return getDefaultValueForField(field);
}

// Inline value editor: pickbox for known enums, plain text input otherwise.
function ValueInput({
    rule,
    customFields,
    projectMembers,
    onChange,
    t,
}: {
    rule: FilterRule;
    customFields: CustomField[];
    projectMembers: Array<{ userId: string; user: { id: string; name: string } }>;
    onChange: (patch: Partial<FilterRule>) => void;
    t: TranslateFn;
}) {
    if (rule.field === 'status') {
        return (
            <Select
                value={typeof rule.value === 'string' ? rule.value : ''}
                onValueChange={(value) => onChange({ value })}
            >
                <SelectTrigger className="w-32">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {/* t12: show translated status labels (TODO / IN_PROGRESS
                        etc. are persisted as enum values; the dropdown
                        surfaces them as "To do" / "En cours" in the user's
                        locale). */}
                    {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                            {getEnumValueLabel('status', s, t)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }
    if (rule.field === 'priority') {
        return (
            <Select
                value={typeof rule.value === 'string' ? rule.value : ''}
                onValueChange={(value) => onChange({ value })}
            >
                <SelectTrigger className="w-32">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {/* t12: translated priority labels. */}
                    {PRIORITY_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>
                            {getEnumValueLabel('priority', p, t)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }
    if (rule.field === 'assignee.id') {
        return (
            <Select
                value={typeof rule.value === 'string' ? rule.value : ''}
                onValueChange={(value) => onChange({ value })}
            >
                <SelectTrigger className="w-40">
                    <SelectValue placeholder="Member" />
                </SelectTrigger>
                <SelectContent>
                    {projectMembers.map((m) => (
                        <SelectItem key={m.userId} value={m.userId}>
                            {m.user.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }
    if (rule.field.startsWith('customField.')) {
        const cfId = rule.field.slice('customField.'.length);
        const cf = customFields.find((c) => c.id === cfId);
        if (cf && (cf.type === 'SELECT' || cf.type === 'MULTI_SELECT' || cf.type === 'SPRINT')) {
            if (rule.operator === 'in' || rule.operator === 'notIn') {
                // Multi-select — pick many options.
                return (
                    <div className="flex flex-wrap items-center gap-1 max-w-xs">
                        {customFieldOptions(cf).map((opt) => {
                            const arr = Array.isArray(rule.value) ? rule.value : [];
                            const checked = arr.includes(opt.id);
                            return (
                                <label key={opt.id} className="flex items-center gap-1 text-xs">
                                    <Checkbox
                                        checked={checked}
                                        onCheckedChange={(c) => {
                                            const next = c
                                                ? Array.from(new Set([...arr, opt.id]))
                                                : arr.filter((v) => v !== opt.id);
                                            onChange({ value: next });
                                        }}
                                    />
                                    <span>{opt.label}</span>
                                </label>
                            );
                        })}
                    </div>
                );
            }
            return (
                <Select
                    value={typeof rule.value === 'string' ? rule.value : ''}
                    onValueChange={(value) => onChange({ value })}
                >
                    <SelectTrigger className="w-40">
                        <SelectValue placeholder="Option" />
                    </SelectTrigger>
                    <SelectContent>
                        {customFieldOptions(cf).map((opt) => (
                            <SelectItem key={opt.id} value={opt.id}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        }
    }
    return (
        <Input
            className="w-32"
            value={typeof rule.value === 'string' ? rule.value : ''}
            onChange={(e) => onChange({ value: e.target.value })}
        />
    );
}
